import React, { useEffect } from 'react';
import AnimatedBackground from '../components/AnimatedBackground';
import { GYM_INFO, STRENGTH_PROGRAMS, GYM_IMAGES } from '../mock';
import { Dumbbell, TrendingUp, Target, Zap } from 'lucide-react';

const StrengthPrograms = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-black pt-24 pb-16">
      <AnimatedBackground />

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={GYM_IMAGES.equipmentArea}
            alt="Strength Training"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent" />
        </div>

        <div className="relative z-10 container mx-auto px-6">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-7xl font-black text-white mb-6 uppercase leading-tight">
              Strength <span className="text-[#39FF14]">Programs</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Build serious muscle and raw power with science-backed strength training programs designed by expert coaches.
            </p>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-6">
        {/* Program Types */}
        <section className="py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto mb-16">
            {[
              { icon: Dumbbell, title: 'Powerlifting', desc: 'Max strength in big 3 lifts' },
              { icon: TrendingUp, title: 'Hypertrophy', desc: 'Muscle size & definition' },
              { icon: Target, title: 'Functional', desc: 'Real-world strength' },
              { icon: Zap, title: 'Athletic', desc: 'Power & explosiveness' },
            ].map((type, idx) => (
              <div
                key={idx}
                className="bg-gradient-to-br from-[#1a1a1a] to-black p-6 rounded-2xl border border-[#39FF14]/20 hover:border-[#39FF14] transition-all duration-300 hover:scale-105 text-center"
              >
                <div className="w-14 h-14 bg-[#39FF14]/20 rounded-xl flex items-center justify-center mb-4 mx-auto">
                  <type.icon className="w-7 h-7 text-[#39FF14]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2 uppercase">{type.title}</h3>
                <p className="text-gray-400 text-sm">{type.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Programs */}
        <section className="py-16">
          <h2 className="text-4xl md:text-5xl font-black text-white text-center mb-12 uppercase">
            Choose Your <span className="text-[#39FF14]">Path</span>
          </h2>

          <div className="space-y-8 max-w-5xl mx-auto">
            {STRENGTH_PROGRAMS.map((program, idx) => (
              <div
                key={program.id}
                className="bg-gradient-to-br from-[#1a1a1a] to-black rounded-3xl border border-[#39FF14]/20 hover:border-[#39FF14] transition-all duration-300 overflow-hidden"
              >
                <div className="grid grid-cols-1 md:grid-cols-3">
                  {/* Program Info */}
                  <div className="md:col-span-2 p-8">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-3xl font-black text-white mb-2 uppercase">
                          {program.name}
                        </h3>
                        <p className="text-[#39FF14] font-bold">{program.duration}</p>
                      </div>
                      <div className="text-right">
                        <div className="text-4xl font-black text-[#39FF14]">₹{program.price}</div>
                      </div>
                    </div>

                    <p className="text-gray-400 mb-6 text-lg">{program.description}</p>

                    <h4 className="text-white font-bold mb-3 uppercase text-sm">What's Included:</h4>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                      {program.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-gray-300 text-sm">
                          <span className="w-1.5 h-1.5 bg-[#39FF14] rounded-full mt-1.5 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* CTA */}
                  <div className="bg-gradient-to-br from-[#39FF14]/5 to-transparent p-8 flex flex-col justify-center items-center border-l border-[#39FF14]/20">
                    <a
                      href={`${GYM_INFO.whatsappLink}?text=Hi, I'm interested in ${program.name} (₹${program.price})`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full px-6 py-4 bg-[#39FF14] text-black font-bold text-center uppercase rounded-full hover:scale-105 transition-transform mb-4"
                    >
                      Enroll Now
                    </a>
                    <p className="text-gray-500 text-xs text-center">
                      Limited spots available
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* What to Expect */}
        <section className="py-16">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-black text-white text-center mb-12 uppercase">
              What You'll <span className="text-[#39FF14]">Achieve</span>
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  title: '2-3X Strength Gains',
                  description: 'Dramatic increases in your major lifts within 12 weeks',
                },
                {
                  title: 'Visible Muscle Growth',
                  description: 'Noticeable size increases in targeted muscle groups',
                },
                {
                  title: 'Injury Prevention',
                  description: 'Proper form and technique to train safely long-term',
                },
              ].map((achievement, idx) => (
                <div
                  key={idx}
                  className="bg-gradient-to-br from-[#1a1a1a] to-black p-6 rounded-2xl border border-[#39FF14]/20 text-center"
                >
                  <div className="text-5xl font-black text-[#39FF14] mb-3">{idx + 1}</div>
                  <h3 className="text-xl font-bold text-white mb-2 uppercase">{achievement.title}</h3>
                  <p className="text-gray-400 text-sm">{achievement.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 text-center">
          <div className="max-w-3xl mx-auto bg-gradient-to-br from-[#1a1a1a] to-black p-12 rounded-3xl border border-[#39FF14]/20">
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6 uppercase">
              Start Building <span className="text-[#39FF14]">Strength</span>
            </h2>
            <p className="text-gray-400 text-lg mb-8">
              Get expert guidance and proven programs to reach your strength goals
            </p>
            <a
              href={`${GYM_INFO.whatsappLink}?text=Hi, I want to join a Strength Program at Iron Crust Gym`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#39FF14] text-black font-bold text-lg uppercase rounded-full hover:scale-105 hover:shadow-2xl hover:shadow-[#39FF14]/50 transition-all duration-300"
            >
              Get Started Today
            </a>
          </div>
        </section>
      </div>
    </div>
  );
};

export default StrengthPrograms;
